import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import React, { memo } from 'react';
import { makeStyles } from 'tss-react/mui';

import type { QcsColor } from 'app-components/flux/transactions/transactionsTypes';
import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';
import FlagField from 'components/Transactions/FlagField';

type VisibilityDisplayState = {
  value: boolean | null | undefined;
  indeterminate: boolean;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(1),
  },
  leftCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  flagButton: {
    // FlagField (plain) renders a ButtonBase child; size it to the 42px design instead of the 56px header default.
    '& > button': {
      height: 42,
      width: 42,
      minWidth: 42,
      padding: 0,
      borderRadius: 12,
    },
  },
  iconButton: {
    height: 42,
    width: 42,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 12,
    color: theme.palette.icon.main,
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  iconButtonActive: {
    // Selected state (drawer): solid primary fill with a white icon.
    borderColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main,
    },
  },
  excludeIcon: {
    height: 24,
    width: 24,
  },
  reviewedButton: {
    height: 42,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    textTransform: 'none',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
    },
  },
  reviewedButtonActive: {
    backgroundColor: theme.palette.success.light,
    borderColor: theme.palette.success.main,
    '&:hover': {
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.light,
    },
  },
  reviewedIconActive: {
    color: theme.palette.amount.positive,
  },
}));

type Props = {
  txnId: string | null | undefined;
  userFlag: QcsColor | null | undefined;
  setFlag: (val: QcsColor | null | undefined) => void;
  isReviewed: boolean | null | undefined;
  updateReviewed: () => void;
  disableReportsValue: boolean;
  visibilityF2SDisplay: VisibilityDisplayState;
  visibilityExcludeReportsDisplay: VisibilityDisplayState;
  setExcludeF2S: (exclude: boolean) => void;
  setExcludeReports: (exclude: boolean) => void;
  /** When splits define exclusions per row, txn-level toggles are read-only (conveyed via tooltip). */
  managedPerSplit: boolean;
  /** Highlights the flag button, e.g. right after an AI suggestion updates it. */
  flagHighlighted?: boolean;
  /** Disables just the flag button, e.g. while an AI cleanup request is in flight. */
  flagDisabled?: boolean;
};

/**
 * Compact review-hygiene row for the redesigned transaction drawer: follow-up flag, the two
 * report/plan exclusion toggles (as icon buttons), and the "Mark as reviewed" action. Wraps the
 * exact same actions/state the legacy header + VisibilitySection used, so behavior is unchanged;
 * only the presentation is condensed.
 */
function FastActionsRow({
  txnId,
  userFlag,
  setFlag,
  isReviewed,
  updateReviewed,
  disableReportsValue,
  visibilityF2SDisplay,
  visibilityExcludeReportsDisplay,
  setExcludeF2S,
  setExcludeReports,
  managedPerSplit,
  flagHighlighted,
  flagDisabled,
}: Props): React.JSX.Element {
  const { classes, cx } = useStyles();

  // Mirror the checked/indeterminate/disabled derivation BottomSection previously fed VisibilitySection.
  const excludeF2S = visibilityF2SDisplay.indeterminate ? null : visibilityF2SDisplay.value;
  const excludeReports = disableReportsValue
    ? true
    : visibilityExcludeReportsDisplay.indeterminate
      ? null
      : visibilityExcludeReportsDisplay.value;

  const f2sDisabled = managedPerSplit;
  const reportsDisabled = managedPerSplit || disableReportsValue;

  const f2sActive = excludeF2S === true || (visibilityF2SDisplay.indeterminate && excludeF2S === null);
  const reportsActive =
    excludeReports === true || (visibilityExcludeReportsDisplay.indeterminate && excludeReports === null);

  const buildTooltip = (excluded: boolean, notExcludedText: string, excludedText: string): string => {
    if (managedPerSplit) return 'Exclusion settings are managed per split';
    return excluded ? excludedText : notExcludedText;
  };

  return (
    <div id='fast-actions-row' className={classes.root}>
      <div className={classes.row}>
        <div className={classes.leftCluster}>
          <FlagField
            plain
            paddedFlagButton
            focusable
            value={userFlag}
            onChange={setFlag}
            className={classes.flagButton}
            highlighted={flagHighlighted}
            disabled={flagDisabled}
          />

          <Tooltip
            title={buildTooltip(f2sActive, 'Click to exclude from Spending Plan', 'Excluded from Spending Plan')}
          >
            <span>
              <IconButton
                id='ignore-spending-plan-toggle'
                aria-label='Exclude from Spending Plan'
                aria-pressed={f2sActive}
                disabled={f2sDisabled}
                className={cx(classes.iconButton, !f2sDisabled && f2sActive && classes.iconButtonActive)}
                onClick={() => setExcludeF2S(!excludeF2S)}
              >
                <ExcludeFromSpendingPlan className={classes.excludeIcon} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            title={buildTooltip(
              reportsActive,
              'Click to exclude from Reports, Watchlists, and Upcoming Summary.',
              'Excluded from Reports, Watchlists, and Upcoming Summary.',
            )}
          >
            <span>
              <IconButton
                id='ignore-reports-toggle'
                aria-label='Exclude from Reports'
                aria-pressed={reportsActive}
                disabled={reportsDisabled}
                className={cx(classes.iconButton, !reportsDisabled && reportsActive && classes.iconButtonActive)}
                onClick={() => setExcludeReports(!excludeReports)}
              >
                <ExcludeFromReports className={classes.excludeIcon} />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        <Button
          id={`${txnId}-reviewed`}
          onClick={updateReviewed}
          className={classNames(classes.reviewedButton, isReviewed && classes.reviewedButtonActive)}
          startIcon={
            isReviewed ? <CheckCircleIcon className={classes.reviewedIconActive} /> : <CheckCircleOutlineIcon />
          }
        >
          {isReviewed ? 'Reviewed' : 'Mark as reviewed'}
        </Button>
      </div>
    </div>
  );
}

export default memo(FastActionsRow);
